import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { quoteService, QuoteRequest, QuoteResult } from "../services/quoteService";

export const quoteQueryKeys = {
  all: ['quotes'] as const,
  byLead: (leadId: string) => [...quoteQueryKeys.all, 'lead', leadId] as const,
};

// Hook to get quotes for a lead
export const useQuotes = (leadId: string) => {
  return useQuery({
    queryKey: quoteQueryKeys.byLead(leadId),
    queryFn: () => quoteService.getQuotes(leadId),
    enabled: !!leadId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: 1000,
  });
};

// Hook to generate quotes
export const useGenerateQuotes = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ leadId, quoteData }: { leadId: string; quoteData: QuoteRequest }) =>
      quoteService.generateQuotes(leadId, quoteData),
    onSuccess: (data, variables) => {
      // Invalidate and refetch quotes for this lead
      queryClient.invalidateQueries({
        queryKey: quoteQueryKeys.byLead(variables.leadId),
      });
    },
  });
};

// Hook to save selected quotes
export const useSaveSelectedQuotes = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ leadId, quoteIds }: { leadId: string; quoteIds: string[] }) =>
      quoteService.saveSelectedQuotes(leadId, quoteIds),
    onSuccess: (data, variables) => {
      // Invalidate quotes to refresh the data
      queryClient.invalidateQueries({
        queryKey: quoteQueryKeys.byLead(variables.leadId),
      });
    },
  });
};

// Hook to export quotes to PDF
export const useExportQuotesToPDF = () => {
  return useMutation({
    mutationFn: ({ leadId, quoteIds }: { leadId: string; quoteIds: string[] }) =>
      quoteService.exportQuotesToPDF(leadId, quoteIds),
  });
};

// Hook to send quotes via email
export const useSendQuotesByEmail = () => {
  return useMutation({
    mutationFn: ({ leadId, quoteIds, email }: { leadId: string; quoteIds: string[]; email: string }) =>
      quoteService.sendQuotesByEmail(leadId, quoteIds, email),
  });
};

export const useTestCompulifeConnection = () => {
  return useMutation({
    mutationFn: () => quoteService.testCompulifeConnection(),
  });
};
